import React from "react"
import PropTypes from "prop-types"

// components
import FavoriteSearchesWidget from "app/static/frontend/widgets/containers/FavoriteSearchesWidget"
import ListWidget from "app/static/frontend/widgets/containers/ListWidget"
import NumberWidget from "app/static/frontend/widgets/containers/NumberWidget"
import QuicklinkWidget from "app/static/frontend/widgets/containers/QuicklinkWidget"
import TextWidget from "app/static/frontend/widgets/containers/TextWidget"
import TrendsWidget from "app/static/frontend/widgets/containers/TrendsWidget"
import VisualizationWidget from "app/static/frontend/widgets/containers/VisualizationWidget"

export class Widget extends React.PureComponent {
    renderWidget(widgetType) {
        const evaluateWidget = (widget) => this.props.evaluateWidget(widget, this.props.widgetKey)

        switch (widgetType) {
            case DjangIO.app.widgets.types.DashboardWidgetType.list_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.calendar_widget.value:
                return (
                    <ListWidget
                        evaluateWidget={evaluateWidget}
                        // StyledWidget props
                        className={this.props.className}
                        dashboardId={this.props.dashboardId}
                        editForm={this.props.editForm}
                        // placeholder that will render if we do not pass a widget schema
                        emptyPlaceholder={this.props.emptyPlaceholder}
                        // title of the widget
                        headerTitle={this.props.headerTitle}
                        // any generic icons that we are passing to the widget
                        headerIcons={this.props.headerIcons}
                        // StyledListWidget props
                        // needed to disable onClick and remove hover state
                        editing={this.props.editing}
                        // needed for widgetEngine call to compute count of QuerySet
                        widget={this.props.widget}
                        // needed to set the id
                        widgetKey={this.props.widgetKey}
                        // used on both StyledListWidget and StyledWidget
                        externalDownloadsDisabled={this.props.externalDownloadsDisabled}
                        isExternal={this.props.isExternal}
                        isCalendarEvent={
                            widgetType === DjangIO.app.widgets.types.DashboardWidgetType.calendar_widget.value
                        }
                    />
                )
            case DjangIO.app.widgets.types.DashboardWidgetType.quicklink_widget.value:
                return (
                    <QuicklinkWidget
                        editForm={this.props.editForm}
                        editing={this.props.editing}
                        headerIcons={this.props.headerIcons}
                        isExternal={this.props.isExternal}
                        widget={this.props.widget}
                        widgetKey={this.props.widgetKey}
                    />
                )
            case DjangIO.app.widgets.types.DashboardWidgetType.number_widget.value:
                return (
                    <NumberWidget
                        cachedData={this.props.cachedData}
                        evaluateWidget={evaluateWidget}
                        editing={this.props.editing}
                        emptyPlaceholder={this.props.emptyPlaceholder}
                        headerIcons={this.props.headerIcons}
                        headerTitle={this.props.headerTitle}
                        isExternal={this.props.isExternal}
                        widget={this.props.widget}
                        widgetKey={this.props.widgetKey}
                    />
                )

            case DjangIO.app.widgets.types.DashboardWidgetType.text_widget.value:
                return (
                    <TextWidget
                        editForm={this.props.editForm}
                        editing={this.props.editing}
                        emptyPlaceholder={this.props.emptyPlaceholder}
                        headerIcons={this.props.headerIcons}
                        headerTitle={this.props.headerTitle}
                        widget={this.props.widget}
                        widgetKey={this.props.widgetKey}
                    />
                )

            case DjangIO.app.widgets.types.DashboardWidgetType.visualization_widget.value:
                return (
                    <VisualizationWidget
                        cachedData={this.props.cachedData}
                        // needed to update values in the store when we click on a specific state or country in the Map
                        change={this.props.change}
                        evaluateWidget={evaluateWidget}
                        editForm={this.props.editForm}
                        editing={this.props.editing}
                        // placeholder that will render if we do not pass a widget schema
                        emptyPlaceholder={this.props.emptyPlaceholder}
                        headerIcons={this.props.headerIcons}
                        headerTitle={this.props.headerTitle}
                        isExternal={this.props.isExternal}
                        widget={this.props.widget}
                        widgetKey={this.props.widgetKey}
                    />
                )

            case DjangIO.app.widgets.types.DashboardWidgetType.favorites_widget.value:
            case DjangIO.app.widgets.types.DashboardWidgetType.recents_widget.value:
                return (
                    <FavoriteSearchesWidget
                        favorites={widgetType === DjangIO.app.widgets.types.DashboardWidgetType.favorites_widget.value}
                        editForm={this.props.editForm}
                        editing={this.props.editing}
                        emptyPlaceholder={this.props.emptyPlaceholder}
                        headerIcons={this.props.headerIcons}
                        headerTitle={this.props.headerTitle}
                        isExternal={this.props.isExternal}
                        widget={this.props.widget}
                        widgetKey={this.props.widgetKey}
                    />
                )
            case DjangIO.app.widgets.types.DashboardWidgetType.trending_widget.value:
                return (
                    <TrendsWidget
                        cachedData={this.props.cachedData}
                        evaluateWidget={evaluateWidget}
                        editForm={this.props.editForm}
                        editing={this.props.editing}
                        emptyPlaceholder={this.props.emptyPlaceholder}
                        headerIcons={this.props.headerIcons}
                        headerTitle={this.props.headerTitle}
                        isExternal={this.props.isExternal}
                        widget={this.props.widget}
                        widgetKey={this.props.widgetKey}
                    />
                )
            default:
                return (
                    <span>
                        {DjangIO.app.widgets.types.DashboardWidgetType.by_value(widgetType).label} is not currently
                        supported!
                    </span>
                )
        }
    }

    render() {
        return this.renderWidget(
            // type from the URI params
            this.props.widgetType ||
                // type from the extant widget meta
                this.props.widget.meta.widget_type,
        )
    }
}

Widget.defaultProps = {}

Widget.propTypes = {
    // in case we have cached data, it can come as a prop (from a dashboard)
    cachedWidgetData: PropTypes.object,
    dashboardId: PropTypes.string,
    // needed to disable onClick and remove hover state
    editing: PropTypes.bool,
    // whether or not this widget is being rendered in the WidgetEditForm
    // this is necessary to disable onClick functionality and hide the Widget-specific header icons
    editForm: PropTypes.bool,
    evaluateWidget: PropTypes.func,
    // whether we are viewing this widget from an external dashboard
    isExternal: PropTypes.bool,
    // this is the source widget schema that is passed to widgetEngine()
    widget: PropTypes.object,
    // this is the widget type that is passed from the URI params through the WidgetEditForm
    widgetType: PropTypes.number,
    // needed to set the id of the Widget component
    widgetKey: PropTypes.string,

    // StyledWidget
    className: PropTypes.string,
    emptyPlaceholder: PropTypes.string,
    headerIcons: PropTypes.array,
    headerTitle: PropTypes.string,

    // from redux-form (see https://redux-form.com/8.1.0/docs/api/props.md/)
    change: PropTypes.func,
}

export default Widget
